import React from 'react';
import { Spinner } from '../../components/UI';
import {
    Button,
    Paper,
    Box,
    Typography,
    IconButton,
    Tooltip,
    TablePagination,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    Divider,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import useVendorManagement from './useVendorManagement';
import { isBlueDartSuperAdmin } from '../../utils/appHelper';
import { VENDOR_MANAGEMENT_TABLE_COLUMNS } from './vendor.helper';
import {
    VendorRow,
    VendorTableCell,
    VendorTableHeadCell,
} from './utility.component.vendor';
import TableSearchBar from '../../components/TableSearchBar';
import VendorModal from '../../components/Modals/VendorModal';
import UploadPayEarnModal from '../../components/Modals/UploadPayEarnModal';

interface VendorManagementPageProps {
    [key: string]: any;
}
const VendorManagementPage: React.FC = ({
    ...props
}: VendorManagementPageProps) => {
    const {
        handleRowClick,
        getColumnComponentValue,
        vendors,
        totalCount,
        pagination,
        handleChangeRowsPerPage,
        onSearchTextChange,
        searchTextValue,
        loading,
        styles,
        handleChangePage,
        openVendorModal,
        closeVendorModal,
        selectedVendor,
        isOpen,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        permissions,
        refetchVendorsData,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    } = useVendorManagement(props);

    const canWriteVendors = permissions.update && isBlueDartSuperAdmin();

    const uploadUIData: any = {
        bulk_vendor: {
            title: 'Bulk Vendor Upload',
            subTitle: ' Upload a xlsx file to add multiple vendors at once.',
            type: 'bulk_vendor',
            enumType: 'bulk_vendor',
            buttonText: 'Upload Vendors',
        },
    };
    // The server can return an empty page with a non-zero total (e.g. pageNo
    // past the end), so the pager must stay mounted in that case.
    const hasVendors = !!vendors?.length;
    const hasResults = hasVendors || totalCount > 0;
    return (
        <Spinner loading={loading}>
            {/* Toolbar: search is submitted explicitly, so typing never queries. */}
            <Box mb={1} className={styles.toolbar}>
                <TableSearchBar
                    placeholder="Search by name, vendor code or contact number"
                    value={searchTextValue}
                    onChange={onSearchTextChange}
                    onSearch={handleSearchSubmit}
                    onClear={handleSearchClear}
                    isDirty={isSearchDirty}
                    hasApplied={!!appliedSearchText}
                    width={380}
                />
                <Box
                    ml="auto"
                    display="flex"
                    alignItems="center"
                    style={{ gap: 8 }}
                >
                    {totalCount > 0 && (
                        <Typography className={styles.resultSummary}>
                            {`${totalCount} vendors`}
                        </Typography>
                    )}
                    <Tooltip title="Refresh" arrow>
                        <IconButton
                            size="small"
                            onClick={() => refetchVendorsData?.()}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {/* Vendor master data is Super Admin only: a vendor org has no zone, and
                        creating one auto-creates its first vendor login, so allowing anyone else
                        here would bypass the Super-Admin-only rule on vendor logins. The backend
                        refuses these, so they are hidden rather than shown disabled. */}
                    {canWriteVendors && (
                        <>
                            <Button
                                variant="outlined"
                                className={styles.outlinedButton}
                                onClick={(e) => {
                                    openBulkUploadModal?.('bulk_vendor');
                                }}
                                disableElevation
                            >
                                {'Bulk Upload Vendor'}
                            </Button>
                            <Button
                                variant="contained"
                                className={styles.primaryButton}
                                onClick={(e) => {
                                    openVendorModal?.();
                                }}
                                disableElevation
                            >
                                {'Add Vendor'}
                            </Button>
                        </>
                    )}
                </Box>
                {!!openBulkModal && (
                    <UploadPayEarnModal
                        open={openBulkModal}
                        data={{}}
                        isEdit={false}
                        enumType={bulkUploadType}
                        onClose={() => {
                            closeBulkUploadModal?.();
                        }}
                        uiData={
                            !!bulkUploadType
                                ? uploadUIData?.[bulkUploadType]
                                : undefined
                        }
                    />
                )}
            </Box>
            <Paper
                className={styles.container}
                elevation={0}
                style={{ padding: '1rem' }}
            >
                {hasResults ? (
                    <>
                        <TableContainer
                            component={Box}
                            className={styles.tableContainer}
                        >
                            <Table stickyHeader aria-label="Vendors table">
                                <TableHead>
                                    <TableRow>
                                        {VENDOR_MANAGEMENT_TABLE_COLUMNS?.map(
                                            (column: any, index: any) => {
                                                return (
                                                    <VendorTableHeadCell
                                                        key={`column${index}`}
                                                    >
                                                        {column?.name}
                                                    </VendorTableHeadCell>
                                                );
                                            },
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {hasVendors ? (
                                        vendors?.map((row: any) => (
                                            <React.Fragment
                                                key={
                                                    row?.uniqueVendorCode ??
                                                    row?.vendorId
                                                }
                                            >
                                                <VendorRow
                                                    row={row}
                                                    columns={
                                                        VENDOR_MANAGEMENT_TABLE_COLUMNS
                                                    }
                                                    getColumnComponentValue={
                                                        getColumnComponentValue
                                                    }
                                                    onClick={handleRowClick}
                                                />
                                                <Divider
                                                    style={{
                                                        margin: '4px 0',
                                                        backgroundColor: '#fff',
                                                    }}
                                                />
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <VendorTableCell
                                                colSpan={
                                                    VENDOR_MANAGEMENT_TABLE_COLUMNS?.length
                                                }
                                            >
                                                <Typography
                                                    component={'p'}
                                                    className={
                                                        styles.emptySubtitle
                                                    }
                                                    style={{
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    No vendors on this page — go
                                                    back to an earlier page.
                                                </Typography>
                                            </VendorTableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box className={styles.paginationContainer}>
                            <TablePagination
                                rowsPerPageOptions={
                                    totalCount > 25 ? [25, 50, 75, 100] : []
                                }
                                component="div"
                                count={totalCount}
                                rowsPerPage={pagination?.pageSize}
                                page={pagination?.pageNo}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Box className={styles.emptyState}>
                        <Typography className={styles.emptyTitle}>
                            No vendors found
                        </Typography>
                        <Typography className={styles.emptySubtitle}>
                            {!!appliedSearchText
                                ? `Nothing matches “${appliedSearchText}”.`
                                : 'There are no vendors to show yet.'}
                        </Typography>
                        {!!appliedSearchText && (
                            <Box mt={1}>
                                <Button
                                    className={styles.ghostButton}
                                    onClick={handleSearchClear}
                                >
                                    Clear search
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
            {!!isOpen && (
                <VendorModal
                    open={isOpen}
                    isBDVendorCreation={true}
                    defaultValue={selectedVendor ?? {}}
                    onClose={() => {
                        closeVendorModal?.();
                    }}
                />
            )}
        </Spinner>
    );
};
export default VendorManagementPage;
